// Client-side image compression.
// Every uploaded picture (avatars, book covers) is resized and re-encoded in the
// browser before it ever reaches storage, so stored files stay small and fast.

export const MAX_UPLOAD_DIMENSION = 1200; // px on the longest edge
export const MAX_UPLOAD_BYTES = 500 * 1024; // 500 KB target

const COMPRESSIBLE = ["image/jpeg", "image/png", "image/webp"];

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read that image.")); };
    img.src = url;
  });
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Resizes to at most MAX_UPLOAD_DIMENSION on the longest edge and steps quality
 * down until the result is under MAX_UPLOAD_BYTES. Returns a new File.
 * GIFs and unsupported types are returned untouched (animation would be lost).
 */
export async function compressImage(
  file: File,
  opts: { maxDimension?: number; maxBytes?: number } = {},
): Promise<File> {
  const maxDimension = opts.maxDimension ?? MAX_UPLOAD_DIMENSION;
  const maxBytes = opts.maxBytes ?? MAX_UPLOAD_BYTES;

  if (typeof document === "undefined") return file;
  if (!COMPRESSIBLE.includes(file.type)) return file;
  if (file.size <= maxBytes) {
    // Still oversized in pixels? Then keep going, otherwise skip the work.
    try {
      const probe = await loadImage(file);
      if (Math.max(probe.width, probe.height) <= maxDimension) return file;
    } catch { return file; }
  }

  let img: HTMLImageElement;
  try { img = await loadImage(file); } catch { return file; }

  const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return file;
  ctx.drawImage(img, 0, 0, width, height);

  const outType = file.type === "image/png" ? "image/png" : "image/jpeg";
  let blob: Blob | null = null;

  if (outType === "image/png") {
    // PNG has no quality knob — re-encode as JPEG when it is still too heavy.
    blob = await toBlob(canvas, "image/png", 1);
    if (!blob || blob.size > maxBytes) blob = null;
  }

  if (!blob) {
    for (const quality of [0.85, 0.75, 0.65, 0.55, 0.45, 0.35, 0.25]) {
      const candidate = await toBlob(canvas, "image/jpeg", quality);
      if (!candidate) continue;
      blob = candidate;
      if (candidate.size <= maxBytes) break;
    }
  }

  if (!blob) return file;
  if (blob.size >= file.size) return file;

  const ext = blob.type === "image/png" ? "png" : "jpg";
  const base = file.name.replace(/\.[^.]+$/, "") || "image";
  return new File([blob], `${base}.${ext}`, { type: blob.type, lastModified: Date.now() });
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
